import { useState } from "react";
import dayjs from "dayjs";
import { useParams, useOutletContext } from "react-router-dom";

import { Modal } from "@components/Page";
import { MODAL_LARGE_WIDTH, MODAL_LARGE_HEIGHT } from "@components/Constants";
import type { CreateScheduleInputMutation, UpdateScheduleInputMutation } from "@api/types";
import { ACTIVE as SCHEDULE_STATUS_ACTIVE } from "@api/services/transactions/shapes/ScheduleStatusEnum";
import { INVESTMENT_RETURN as INVESTMENT_RETURN_TRANSACTION_TYPE } from "@api/services/transactions/shapes/TransactionTypeEnum";
import { MONTHLY as DISTRIBUTION_FREQUENCY_MONTHLY } from "@api/services/transactions/shapes/DistributionFrequencyEnum";

import DistributionsScheduleForm, { getNextDate } from "./DistributionsScheduleForm";
import type { Investment, OutletContext, ScheduleFormValues, Schedule } from "./types";

const LABEL_TITLE = "Distributions Schedule";

interface ModalHandle {
  current: {
    open: () => void;
    close: () => void;
  };
}

type Props = {
  modal: ModalHandle;
  schedule?: Schedule;
  investments: Investment[];
  createSchedule: (mutation: CreateScheduleInputMutation) => Promise<unknown>;
  updateSchedule: (scheduleId: string, mutation: UpdateScheduleInputMutation) => Promise<unknown>;
  deleteSchedule: (scheduleId: string) => Promise<unknown>;
  enableSchedule: (scheduleId: string) => Promise<unknown>;
  disableSchedule: (scheduleId: string) => Promise<unknown>;
}


const DistributionsScheduleModal = ({
  schedule = undefined,
  modal,
  investments,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  enableSchedule,
  disableSchedule,
}: Props) => {
  const [ isOpen, setIsOpen ] = useState(false);

  const onClose = () =>
    setIsOpen(false);

  modal.current.open = () =>
    setIsOpen(true);

  modal.current.close = () =>
    onClose();

  const { id: projectId } = useParams();
  const { project } = useOutletContext<OutletContext>();
  const { name } = project;

  const initialValues: ScheduleFormValues = schedule
    ? {
        name: schedule.name,
        startDate: schedule.startDate,
        nextDate: schedule.nextDate,
        shouldRoundCents: schedule.shouldRoundCents,
        distributionFrequency: schedule.distributionFrequency,
        status: schedule.status,
        transactionType: INVESTMENT_RETURN_TRANSACTION_TYPE,
        projectId: schedule.projectId,
        distributions: schedule.distributions,
      }
    : {
        name,
        nextDate: getNextDate(dayjs().toISOString(), DISTRIBUTION_FREQUENCY_MONTHLY),
        startDate: dayjs().toISOString(),
        shouldRoundCents: false,
        distributionFrequency: DISTRIBUTION_FREQUENCY_MONTHLY,
        status: SCHEDULE_STATUS_ACTIVE,
        transactionType: INVESTMENT_RETURN_TRANSACTION_TYPE,
        projectId,
      };

  const modalProps = {
    title: LABEL_TITLE,
    width: MODAL_LARGE_WIDTH,
    destroyOnClose: true,
    isOpen,
    onClose,
  };

  const formProps = {
    scheduleId: schedule?.id,
    onClose,
    investments,
    initialValues,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    enableSchedule,
    disableSchedule,
  };

  return (
    <Modal key={projectId} {...modalProps}>
      <div style={{ minHeight: MODAL_LARGE_HEIGHT }}>
        <DistributionsScheduleForm {...formProps} />
      </div>
    </Modal>
  );
};

export default DistributionsScheduleModal;
